/**
 * A piece type's param form, driven by its param specs (D22,
 * `paramSpecSchema`): what the form holds while an operator fills it in (a
 * DRAFT per param, as deep as the spec), and the values a filled-in form
 * sends — checked as the production will check them (`@norna/playout-engine/params.js`),
 * so a bad value is shown beside its field instead of coming back as
 * `invalid-params`.
 *
 *   string / number / integer   a text draft (an `enum` is a picker over the same text)
 *   boolean                      a checkbox
 *   object                       a draft per property (its own fields, nested)
 *   array                        a list of item drafts, added and removed by the operator
 *
 * **Absent vs empty.** A blank optional field is left out, so the production
 * applies the spec's default. An optional object nobody touched (every field
 * blank, every checkbox at its default, every list empty) is left out whole —
 * its own required fields are not asked for, as the production does not ask
 * for them when the object is absent. An optional list with no items is left
 * out; a required one is sent empty (`[]`, which `minItems` may refuse). A
 * required text field that is blank is an error, as the production would say.
 * A list ELEMENT is always present: a blank element is an error, never a hole.
 *
 * Errors are keyed by the field's path (`person.name`, `lines[2]`), which is
 * also how the form names its controls.
 */
import type { ParamSpec } from '@norna/playout-engine/productionConfig.js';
import type { JsonValue } from '@norna/domain/primitives.js';

export type Draft =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'bool'; readonly value: boolean }
  | { readonly kind: 'object'; readonly fields: Readonly<Record<string, Draft>> }
  | { readonly kind: 'array'; readonly items: readonly Draft[] };

/** One top-level param of the form. */
export type ParamField = {
  readonly name: string;
  readonly label: string;
  readonly spec: ParamSpec;
  /** The draft the form starts from: the item's value, else the spec's default, else blank. */
  readonly initial: Draft;
};

function isRecord(value: JsonValue | undefined): value is { [key: string]: JsonValue } {
  return value !== undefined && value !== null && typeof value === 'object' && !Array.isArray(value);
}

function textOf(value: JsonValue | undefined): string {
  if (value === undefined || value === null) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/** The draft a spec starts from, filled from `value` (an item's param) or the spec's default. */
export function draftOf(spec: ParamSpec, value: JsonValue | undefined): Draft {
  const v = value ?? spec.default ?? undefined;
  switch (spec.type) {
    case 'boolean':
      return { kind: 'bool', value: v === true };
    case 'object': {
      const given = isRecord(v) ? v : {};
      const fields: Record<string, Draft> = {};
      for (const [key, child] of Object.entries(spec.properties)) fields[key] = draftOf(child, given[key]);
      return { kind: 'object', fields };
    }
    case 'array':
      return { kind: 'array', items: Array.isArray(v) ? v.map((item) => draftOf(spec.items, item)) : [] };
    default:
      return { kind: 'text', text: textOf(v) };
  }
}

/** A fresh element for a list whose items are `spec` (its default, or blank). */
export function emptyDraft(spec: ParamSpec): Draft {
  return draftOf(spec, undefined);
}

/** The form's fields for a piece type, filled from `values` (an item's params) or the specs' defaults. */
export function paramFields(specs: Readonly<Record<string, ParamSpec>>, values: Readonly<Record<string, JsonValue>>): ParamField[] {
  return Object.entries(specs).map(([name, spec]) => ({ name, label: labelOf(name, spec), spec, initial: draftOf(spec, values[name]) }));
}

/** What a field shows as its label: the spec's, else its key. */
export function labelOf(name: string, spec: ParamSpec): string {
  return spec.label === '' ? name : spec.label;
}

/** Whether a field must be filled in: required, with no default to fall back on. */
export function mustFill(spec: ParamSpec): boolean {
  return spec.required && spec.default === null;
}

// ─── parsing ───

type Parsed = { readonly value: JsonValue } | { readonly skip: true } | { readonly failed: true };

type Errors = Record<string, string>;

function fail(errors: Errors, path: string, message: string): Parsed {
  errors[path] = message;
  return { failed: true };
}

function parseString(spec: Extract<ParamSpec, { type: 'string' }>, raw: string, path: string, errors: Errors): Parsed {
  if (spec.enum !== null && !spec.enum.includes(raw)) return fail(errors, path, `one of ${spec.enum.join(', ')}`);
  if (spec.maxLength !== null && raw.length > spec.maxLength) return fail(errors, path, `at most ${String(spec.maxLength)} characters`);
  return { value: raw };
}

function parseNumber(spec: Extract<ParamSpec, { type: 'number' | 'integer' }>, raw: string, path: string, errors: Errors): Parsed {
  const n = Number(raw.trim());
  if (!Number.isFinite(n)) return fail(errors, path, 'not a number');
  if (spec.type === 'integer' && !Number.isInteger(n)) return fail(errors, path, 'a whole number');
  if (spec.enum !== undefined && !spec.enum.includes(n)) return fail(errors, path, `one of ${spec.enum.join(', ')}`);
  if (spec.minimum !== null && n < spec.minimum) return fail(errors, path, `at least ${String(spec.minimum)}`);
  if (spec.maximum !== null && n > spec.maximum) return fail(errors, path, `at most ${String(spec.maximum)}`);
  return { value: n };
}

function parseText(spec: Extract<ParamSpec, { type: 'string' | 'number' | 'integer' }>, raw: string, path: string, errors: Errors, present: boolean): Parsed {
  if (raw.trim() === '') return present || mustFill(spec) ? fail(errors, path, 'required') : { skip: true };
  return spec.type === 'string' ? parseString(spec, raw, path, errors) : parseNumber(spec, raw, path, errors);
}

/** Whether a draft is as the form started it with nothing given: blank text, an empty list, a checkbox at its default. */
function untouched(spec: ParamSpec, draft: Draft): boolean {
  switch (draft.kind) {
    case 'text':
      return draft.text.trim() === '';
    case 'bool':
      return draft.value === (spec.default === true);
    case 'array':
      return draft.items.length === 0;
    case 'object':
      return spec.type === 'object' && Object.entries(spec.properties).every(([key, child]) => untouched(child, draft.fields[key] ?? emptyDraft(child)));
  }
}

function parseObject(spec: Extract<ParamSpec, { type: 'object' }>, draft: Draft, path: string, errors: Errors, present: boolean): Parsed {
  // An optional object nobody filled in is absent: its required fields are the object's business only when it is there.
  if (!present && !mustFill(spec) && untouched(spec, draft)) return { skip: true };
  const fields = draft.kind === 'object' ? draft.fields : {};
  const out: Record<string, JsonValue> = {};
  let failed = false;
  for (const [key, child] of Object.entries(spec.properties)) {
    const result = parseDraft(child, fields[key] ?? emptyDraft(child), `${path}.${key}`, errors, false);
    if ('failed' in result) failed = true;
    else if ('value' in result) out[key] = result.value;
  }
  return failed ? { failed: true } : { value: out };
}

function parseArray(spec: Extract<ParamSpec, { type: 'array' }>, draft: Draft, path: string, errors: Errors, present: boolean): Parsed {
  const items = draft.kind === 'array' ? draft.items : [];
  // An optional list with nothing in it is absent; a required one is sent empty, and `minItems` says if that is enough.
  if (items.length === 0 && !present && !mustFill(spec)) return { skip: true };
  if (spec.minItems !== null && items.length < spec.minItems) return fail(errors, path, `at least ${String(spec.minItems)} item(s)`);
  if (spec.maxItems !== null && items.length > spec.maxItems) return fail(errors, path, `at most ${String(spec.maxItems)} item(s)`);
  const out: JsonValue[] = [];
  let failed = false;
  items.forEach((item, index) => {
    const result = parseDraft(spec.items, item, `${path}[${String(index)}]`, errors, true);
    if ('value' in result) out.push(result.value);
    else failed = true;
  });
  return failed ? { failed: true } : { value: out };
}

/**
 * One draft's value, typed by its spec. `present`: the value must be there
 * (a list element) — blank is then an error rather than "left out".
 */
function parseDraft(spec: ParamSpec, draft: Draft, path: string, errors: Errors, present: boolean): Parsed {
  switch (spec.type) {
    case 'boolean':
      return { value: draft.kind === 'bool' && draft.value };
    case 'object':
      return parseObject(spec, draft, path, errors, present);
    case 'array':
      return parseArray(spec, draft, path, errors, present);
    default:
      return parseText(spec, draft.kind === 'text' ? draft.text : '', path, errors, present);
  }
}

export type ParamParse = { readonly ok: true; readonly params: Record<string, JsonValue> } | { readonly ok: false; readonly errors: Readonly<Record<string, string>> };

/** The params a filled-in form sends, or every field's error by path. */
export function parseParams(fields: readonly ParamField[], drafts: Readonly<Record<string, Draft>>): ParamParse {
  const params: Record<string, JsonValue> = {};
  const errors: Errors = {};
  for (const field of fields) {
    const result = parseDraft(field.spec, drafts[field.name] ?? field.initial, field.name, errors, false);
    if ('value' in result) params[field.name] = result.value;
  }
  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, params };
}
